import React from 'react'
import {mixed, object, number, date, string, bool} from 'yup'
import {cnpj as cnpjValidator, cpf as cpfValidator} from 'cpf-cnpj-validator'

// material-ui core components
import {Grid} from '@material-ui/core'
import {withStyles} from '@material-ui/core/styles'
import CustomDialog from '../../components/CustomDialog'

import FormStepper from '../../components/FormStepper'
import FormikStep from '../../components/FormikStep'
import {INVALID_STATEMENT, REQUIRED_STATEMENT} from '../../constants'
import {preRegister} from '../../services/registration'
import {createCompany, createSubAccount, uploadCompanyDocument} from '../../services/users'
import {dataURItoBlob} from '../../utils'
import SubAccount from '../SubAccount'
import TermsOfUse from '../TermsOfUse'

import {
  Step1,
  Step3,
  Step4,
  Step5,
  Step7,
  Step8,
  Step9,
  Step10,
  Step11,
  Step12,
  Step13,
  Step14,
  Step15
} from './Steps'
import Finish from '../Finish'


const styles = (theme) => ({
  home: {
    [theme.breakpoints.up('md')]: {
      marginTop: 80
    },
    marginTop: 10
  },
})

const Company = ({classes}) => {
  const [open, setOpen] = React.useState(false);
  const [registered, setRegistered] = React.useState(false)
  const [errorCode, setErrorCode] = React.useState(null);
  const sendDocument = async (values, helpers, step) => {
    try {
      const {cnpj, documentType, image} = values
      const imageBlob = dataURItoBlob(image)
      const formData = new FormData();
      formData.append('image', imageBlob);
      await uploadCompanyDocument(cnpj, documentType, formData)
      return (step + 1)
    } catch (e) {
      const {data} = e.response
      const {code} = data
      setErrorCode(code)
      setOpen(true)
      return step
    }
  }
  return (
    <Grid
      component="div"
      className={classes.home}
      container
      spacing={2}
      justify="center"
      alignContent="center"
      alignItems="center"
    >
      <Grid item xs={10} md={3} component="div">
        <CustomDialog
          open={open}
          onClose={() => setOpen(false)}
          errorCode={errorCode}
          title="Não é possível realizar o cadastro"
        />
        <FormStepper
          initialValues={{
            cnpj: '',
            email: '',
            username: '',
            termsAccepted: false,
            notifyAccepted: false,
            legalName: '',
            tradeName: '',
            password: '',
            legalNature: '',
            establishmentDate: null,
            establishmentFormat: '',
            mainCnae: '',
            revenue: '',
            partnerChanged: false,
            zip: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            federativeUnit: '',
            country: '',

            partnerCpf: '',
            partnerName: '',
            motherName: '',
            partnerRevenue: '',
            birthDate: null,
            partnerEmail: '',
            phone: '',
            isPep: false,
            isPepSince: null,
            maritalStatus: '',
            documentType: '',
            image: '',
            sponsor: '',
            partner: false
          }}
          showSteps
          datakey="companyData"
          showControls
        >
          <FormikStep
            validationSchema={object({
              cnpj: string().test('cnpj-is-valid', INVALID_STATEMENT, cnpjValidator.isValid)
                .required(REQUIRED_STATEMENT),
              email: string().email(INVALID_STATEMENT)
                .required(REQUIRED_STATEMENT),
              username: string().matches(/^[a-z0-9A-Z]+$/, INVALID_STATEMENT).required(REQUIRED_STATEMENT),
              sponsor: string().required(REQUIRED_STATEMENT)
            })}
            onSubmit={async (values, helpers, step) => {
              try {
                const {cnpj, email, username, sponsor} = values
                const {data} = await preRegister(cnpj, email, username, sponsor, false)
                const {code} = data
                if (code === 2008) {
                  setRegistered(true)
                  return 1
                } else if (code === 2002) {
                  setErrorCode(code)
                  setOpen(true)
                  return 10
                }
                return (step + 1)
              } catch (e) {
                const { code } = e.response.data
                setErrorCode(code)
                setOpen(true)
                return step
              }
            }}
          >
            <Step1 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              termsAccepted: bool().oneOf([true], REQUIRED_STATEMENT),
            })}
          >
            <TermsOfUse />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              password: string().matches(/^[0-9]+$/, 'A senha deve ser numérica')
                .required(REQUIRED_STATEMENT)
                .min(6, 'Digite uma senha com 6 dígitos'),
            })}
            onSubmit={(values, helpers, step) => {
              if (registered) return 10
              return (step + 1)
            }}
          >
            <Step3 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              legalName: string().required(REQUIRED_STATEMENT),
              phantasyName: string().required(REQUIRED_STATEMENT),
              establishmentFormat: string().required(REQUIRED_STATEMENT),
              revenue: number().required(REQUIRED_STATEMENT)
            })}
          >
            <Step4 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              establishmentDate: date().required(REQUIRED_STATEMENT).nullable()
            })}
          >
            <Step5 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              zip: string().required(REQUIRED_STATEMENT),
              street: string().max(40, 'Não deve ultrapassar 40 caracteres.').required(REQUIRED_STATEMENT),
              number: string().matches(/^(0|[1-9][0-9]*[A-Za-z]{0,2})$/, 'Não deve iniciar com 0.').required(REQUIRED_STATEMENT),

              complement: string(),
              neighborhood: string().required(REQUIRED_STATEMENT),
              city: string().required(REQUIRED_STATEMENT),
              federativeUnit: string().required(REQUIRED_STATEMENT),
              country: string().required(REQUIRED_STATEMENT),
            })}
          >
            <Step7 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              phone: string().required(REQUIRED_STATEMENT)
            })}
          >
            <Step8 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              isPep: bool(),
              isPepSince: mixed().when('isPep', {
                is: true,
                then: date().required(REQUIRED_STATEMENT)
              }),
              maritalStatus: number().required(REQUIRED_STATEMENT),
              gender: string().required(REQUIRED_STATEMENT)
            })}
          >
            <Step9 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              partnerCpf: string().test('cpf-is-valid', INVALID_STATEMENT, cpfValidator.isValid)
                .required(REQUIRED_STATEMENT),
              partnerName: string().required(REQUIRED_STATEMENT),
              motherName: string().required(REQUIRED_STATEMENT),
            })}
          >
            <Step10 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              birthDate: date().required(REQUIRED_STATEMENT),
              partnerRevenue: string().required(REQUIRED_STATEMENT),
              partnerEmail: string().email(INVALID_STATEMENT)
                .required(REQUIRED_STATEMENT),
            })}
            onSubmit={async (values, helpers, step) => {
            const {userAgent} = window.navigator
            try {
              await createCompany({...values, userAgent})
              return (step + 1)
            } catch (e) {
              const {data} = e.response
              const {code} = data
              setErrorCode(code)
              setOpen(true)
              return step
            }
          }}>
            <Step11 />
          </FormikStep>
          {registered && (
            <FormikStep
              submitText="Cadastrar"
              onSubmit={async (values, helpers, step) => {
                try {
                  await createSubAccount({...values, partner: false})
                  return (step + 1)
                } catch (e) {
                  const { code } = e.response.data
                  setErrorCode(code)
                  setOpen(true)
                  return step
                }
              }}
            >
              <SubAccount />
            </FormikStep>
          )}
          <FormikStep
            validationSchema={object({
              documentType: string().required(REQUIRED_STATEMENT),
              image: string().required(REQUIRED_STATEMENT),
            })}
            saveFields={false}
            onSubmit={sendDocument}>
            <Step12 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              documentType: string().required(REQUIRED_STATEMENT),
              image: string().required(REQUIRED_STATEMENT),
            })}
            saveFields={false}
            onSubmit={sendDocument}>
            <Step13 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              documentType: string().required(REQUIRED_STATEMENT),
              image: string().required(REQUIRED_STATEMENT),
            })}
            saveFields={false}
            onSubmit={sendDocument}>
            <Step14 />
          </FormikStep>
          <FormikStep
            validationSchema={object({
              documentType: string().required(REQUIRED_STATEMENT),
              image: string().required(REQUIRED_STATEMENT),
            })}
            saveFields={false}
            onSubmit={sendDocument}>
            <Step15 />
          </FormikStep>
          <Finish showControls={false} />
        </FormStepper>
      </Grid>
    </Grid>
  )
}

Company.propTypes = {

};

export default withStyles(styles)(Company);
